import { zoneOffsetSecondsAt } from "../time-zone-rules";

// Solar position for trail-level aspect scoring.
//
// NOAA solar position approximation (Meeus-derived, accuracy ~0.2 degrees:
// ample for slope-incidence modulation). No dependencies. Azimuth uses the
// compass convention (0 = north, clockwise), matching trail aspect_deg.

export type SolarPosition = {
  azimuthDeg: number;
  elevationDeg: number;
};

const DEG = Math.PI / 180;

function toJulianDay(date: Date): number {
  return date.getTime() / 86_400_000 + 2_440_587.5;
}

/**
 * Sun azimuth/elevation for a UTC instant at the given coordinates.
 */
export function solarPosition(lat: number, lng: number, utc: Date): SolarPosition {
  const jd = toJulianDay(utc);
  const t = (jd - 2_451_545) / 36_525; // Julian centuries from J2000

  // Geometric mean longitude and anomaly of the sun (degrees).
  const meanLong = (280.46646 + t * (36_000.76983 + t * 0.0003032)) % 360;
  const meanAnom = 357.52911 + t * (35_999.05029 - 0.0001537 * t);

  // Equation of centre -> true longitude.
  const centre =
    Math.sin(meanAnom * DEG) * (1.914602 - t * (0.004817 + 0.000014 * t)) +
    Math.sin(2 * meanAnom * DEG) * (0.019993 - 0.000101 * t) +
    Math.sin(3 * meanAnom * DEG) * 0.000289;
  const trueLong = meanLong + centre;

  // Apparent longitude (nutation/aberration correction).
  const omega = 125.04 - 1934.136 * t;
  const apparentLong = trueLong - 0.00569 - 0.00478 * Math.sin(omega * DEG);

  // Obliquity of the ecliptic (corrected).
  const meanObliquity =
    23 + (26 + (21.448 - t * (46.815 + t * (0.00059 - t * 0.001813))) / 60) / 60;
  const obliquity = meanObliquity + 0.00256 * Math.cos(omega * DEG);

  // Solar declination.
  const declination = Math.asin(
    Math.sin(obliquity * DEG) * Math.sin(apparentLong * DEG),
  );

  // Equation of time (minutes).
  const y = Math.tan((obliquity / 2) * DEG) ** 2;
  const eqTimeMin =
    4 *
    (1 / DEG) *
    (y * Math.sin(2 * meanLong * DEG) -
      2 * 0.016708634 * Math.sin(meanAnom * DEG) +
      4 * 0.016708634 * y * Math.sin(meanAnom * DEG) * Math.cos(2 * meanLong * DEG) -
      0.5 * y * y * Math.sin(4 * meanLong * DEG) -
      1.25 * 0.016708634 ** 2 * Math.sin(2 * meanAnom * DEG));

  // True solar time (minutes) -> hour angle (degrees; solar noon = 0).
  const utcMinutes =
    utc.getUTCHours() * 60 + utc.getUTCMinutes() + utc.getUTCSeconds() / 60;
  const trueSolarMin = (utcMinutes + eqTimeMin + 4 * lng + 1440) % 1440;
  const hourAngle = trueSolarMin / 4 - 180;

  // Elevation.
  const latRad = lat * DEG;
  const haRad = hourAngle * DEG;
  const cosZenith =
    Math.sin(latRad) * Math.sin(declination) +
    Math.cos(latRad) * Math.cos(declination) * Math.cos(haRad);
  const zenith = Math.acos(Math.min(1, Math.max(-1, cosZenith)));
  const elevationDeg = 90 - zenith / DEG;

  // Azimuth (compass, 0 = north, clockwise).
  const sinZenith = Math.sin(zenith);
  let azimuthDeg: number;
  if (Math.abs(sinZenith) < 1e-6) {
    azimuthDeg = 180; // sun at zenith/nadir: azimuth undefined, use south
  } else {
    const cosAz =
      (Math.sin(latRad) * Math.cos(zenith) - Math.sin(declination)) /
      (Math.cos(latRad) * sinZenith);
    const az = Math.acos(Math.min(1, Math.max(-1, cosAz))) / DEG;
    azimuthDeg = hourAngle > 0 ? (az + 180) % 360 : (540 - az) % 360;
  }

  return { azimuthDeg, elevationDeg };
}

/**
 * UTC instant for a civil local time in an IANA timezone, dependency-free.
 * Two-pass: guess the offset from a UTC interpretation, then correct.
 */
export function utcFromLocal(
  dateYmd: string,
  hour: number,
  timeZone: string,
): Date {
  const [y, m, d] = dateYmd.split("-").map(Number);
  const wholeHour = Math.floor(hour);
  const minutes = Math.round((hour - wholeHour) * 60);
  const guess = new Date(Date.UTC(y, m - 1, d, wholeHour, minutes));

  // ms the zone is ahead of UTC, from lib/time-zone-rules.ts (tzdata 2026e over the runtime's data).
  const offsetAt = (instant: Date): number => zoneOffsetSecondsAt(instant.getTime(), timeZone) * 1000;

  const first = new Date(guess.getTime() - offsetAt(guess));
  // Second pass handles instants near a DST transition.
  return new Date(guess.getTime() - offsetAt(first));
}

/**
 * Civil sunset hour (local decimal hours) for a date and place: when the
 * sun's elevation crosses zero in the afternoon. Scans at 5-minute
 * resolution, which is plenty for itinerary planning.
 */
export function sunsetHour(
  lat: number,
  lng: number,
  dateYmd: string,
  timeZone: string,
): number | null {
  for (let h = 12; h <= 23.99; h += 5 / 60) {
    const pos = solarPosition(lat, lng, utcFromLocal(dateYmd, h, timeZone));
    if (pos.elevationDeg <= 0) return Math.round(h * 100) / 100;
  }
  return null; // polar day; not a planner concern in the Alps
}

/**
 * Civil sunrise hour (local decimal hours).
 */
export function sunriseHour(
  lat: number,
  lng: number,
  dateYmd: string,
  timeZone: string,
): number | null {
  for (let h = 0; h <= 12; h += 5 / 60) {
    const pos = solarPosition(lat, lng, utcFromLocal(dateYmd, h, timeZone));
    if (pos.elevationDeg > 0) return Math.round(h * 100) / 100;
  }
  return null;
}
